import React, { useState } from 'react'
import GoogleMapReact from 'google-map-react'
import { observer } from 'mobx-react-lite'
import { useNeomem } from '../application'
import { useGlobals } from '../application'
import * as util from './util'
import './styles.css'

// import pin from './images/pin.svg'
// import pin from './images/flag.svg'
// pinsfrom https://sites.google.com/site/gmapsdevelopment/
import pinGreen from './images/mm_20_green.png'
import pinRed from './images/mm_20_red.png'

const keys = { key: process.env.REACT_APP_MAP_KEY }

// default map view - center over usa
//. tried defaulting to world, but map has minzoom due to bad calcs
// see https://github.com/google-map-react/google-map-react/blob/master/API.md#override-the-default-minimum-zoom
const defaultView = {
  center: { lat: 37, lng: -94 },
  zoom: 4,
}

// we use this to save marker and line references between renderings
let save = {}


//----------------------------------------------------------------------
// component
//----------------------------------------------------------------------

export default observer(() => {

  // local state
  const [map, setMap] = useState(null)
  const [maps, setMaps] = useState(null)
  const [infoWindow, setInfoWindow] = useState(null)

  // global state
  const neomem = useNeomem()
  const globals = useGlobals()
  if (neomem.initializing) return null

  // local vars
  const currentNodeId = neomem.currentNodeId //. mobx being weird, had to use this
  if (!currentNodeId) return null
  const currentNode = neomem.getNode(currentNodeId)
  if (!currentNode) return null // bail if initializing
  const places = getPlacesToShow(currentNodeId, neomem)
  console.log('map places:', places)
  // addPlaceCoordinates(places)
  const showLines = getShowLines(neomem, currentNodeId)

  // if map api is loaded, draw the current places and lines
  const mapdiv = document.getElementById('map')
  let view = defaultView
  if (maps && map && mapdiv) {

    const newMarkers = drawMarkers(places)
    const newLines = drawLines(places, showLines)
    save.markers = newMarkers && [...newMarkers]
    save.lines = newLines && [...newLines]

    view = util.getView(places, mapdiv, defaultView)

    //. any way to get smooth panning/zooming? i think you have to do in timing loop
    map.setZoom(view.zoom)
    map.panTo(view.center)
  }

  //----------------------------------------------------------------------
  // functions
  //----------------------------------------------------------------------

  function getPlacesToShow(nodeId, neomem) {
    let places
    if (currentNodeId === 'welcome') { //.
      places = []
    } else if (neomem.hasChildren(currentNodeId)) {
      places = neomem.getChildren(currentNodeId)
    } else {
      places = neomem.getSiblings(currentNodeId)
    }
    // skip nodes without bounds or centers (eg Trips)
    places = places.filter(place => place.boundsStr || place.centerStr)
    addPlaceCoordinates(places)
    return places
  }

  function addPlaceCoordinates(places) {
    for (let place of places) {
      if (place.boundsStr && !place.bounds) {
        place.bounds = util.getBoundsFromString(place.boundsStr)
      }
      if (place.centerStr && !place.center) {
        place.center = util.getCenterFromString(place.centerStr) // lat,lng
        // maps api needs lat lng for each point
        place.lat = place.center.lat
        place.lng = place.center.lng
      }
    }
  }

  // should we show lines between the current places?
  function getShowLines(neomem, currentNodeId) {
    // get subtypes - eg country, state, city, establishment, place
    const currentSubtypeId = neomem.getNodeValue(currentNodeId, 'subtypeId') || 'place'
    const parentNodeId = neomem.getNodeValue(currentNodeId, 'parentId')
    const parentSubtypeId = parentNodeId ? neomem.getNodeValue(parentNodeId, 'subtypeId') || 'place' : 'place'
    // rules
    const hasChildren = neomem.hasChildren(currentNodeId)
    const isCityWithChildren = currentSubtypeId === 'city' && hasChildren
    const isChildOfCity = parentSubtypeId === 'city'
    const showLines = isCityWithChildren || isChildOfCity ? 0 : 1
    return showLines
  }

  // callback for google-map-react component
  //. what else does this receive?
  const handleApiLoaded = ({ map, maps }) => {
    console.log('handleApiLoaded - make infowindow')
    // save map refs locally
    setMap(map)
    setMaps(maps)
    // make a single infowindow to re-use for all markers
    const infoWindow = new maps.InfoWindow()
    setInfoWindow(infoWindow)
    map.addListener('click', () => infoWindow.close())
    // make google maps api avail in other parts of app, eg sidebar
    globals.map = map
    globals.maps = maps
  }

  //. could use this to update trip's view, but if wrote to firestore every time would be expensive.
  //. so write every n times though?
  //. oh, just save to nm cache, don't persist it
  const handleChange = ({ bounds }) => {
    // { bounds: {ne, nw, se, sw}, center: {lat, lng}, marginBounds, size: {height px, width px}, zoom: Number }
    // console.log(bounds)
  }

  //. code duplicated in navtree! move into neomem
  const removeNode = (nodeId) => {
    const node = neomem.getNode(nodeId)
    if (window.confirm(`Remove ${node.name} and any subitems and notes? This can't be undone!`)) {
      // first select the parent node so we don't point to a non-existent node
      const parent = neomem.getParent(nodeId)
      neomem.setCurrentNodeId(parent.id)
      neomem.deleteNode(nodeId)
    }
  }

  // create an infobox for a place
  //. could we use react to render? ehh
  //. how do sthing like this?
  // const content = `
  //   <div class="infobox">
  //     <div class="infobox-name">${place.name}</div>
  //     <div class="infobox-remove">Remove</div>
  //   </div>
  // `
  //. could add part of notes for place with edit btn
  function makeInfobox(infobox, marker, place, removeNode, map) {
    const content = document.createElement('div')
    const name = document.createElement('div')
    const notes = document.createElement('div')
    const remove = document.createElement('div')
    content.className = 'infobox'
    name.className = 'infobox-name'
    notes.className = 'infobox-notes'
    remove.className = 'infobox-remove'
    name.innerText = place.name
    notes.innerText = place.notes ? place.notes.slice(0,40) + '...' : ''
    remove.innerText = 'Remove'
    remove.addEventListener('click', () => removeNode(place.id))
    content.appendChild(name)
    content.appendChild(notes)
    content.appendChild(remove)
    infobox.setContent(content)
    infobox.open(map, marker)
  }

  const handleMarkerClick = (marker, place) => {
    makeInfobox(infoWindow, marker, place, removeNode, map)
    neomem.setCurrentNodeId(place.id)
  }

  const handleMarkerHover = (marker, place) => {
    makeInfobox(infoWindow, marker, place, removeNode, map)
  }

  // function drawMarkers(places) { util.drawMarkers(places, maps, map) }
  // function drawLines(places, showLines){ util.drawLines(places, showLines, maps, map) }

  //. move to util
  // this makes a closure on maps, map, markers
  //. better would be do a diff? then no flickering
  function drawMarkers(places) {
    const newMarkers = places && places.map((place, index) => {
      const marker = new maps.Marker({
        position: place.center,
        map,
        title: place.name,
        icon: (place.id === currentNodeId) ? pinGreen : pinRed,
        // draggable: true,
        // scale: 1.5, // nowork?
        // scaledSize: new maps.Size(38, 38), // nowork
      })
      // marker.addListener('click', () => makeInfobox(infoWindow, marker, place, removeNode, map))
      marker.addListener('click', () => handleMarkerClick(marker, place))
      marker.addListener('mouseover', () => handleMarkerHover(marker, place))
      // marker.addListener('mouseout', () => infoWindow.close())
      return marker
    })
    if (save.markers) {
      save.markers.forEach(marker => marker.setMap(null)) // remove old markers
    }
    return newMarkers
  }

  // this makes a closure on maps, map, lines
  // const drawLines = (places, linesVisible=true) => {
  function drawLines(places, linesVisible=true) {
    // see https://stackoverflow.com/questions/31305497/how-to-draw-an-arrow-on-every-polyline-segment-on-google-maps-v3
    const newLines = []
    if (places) {
      for (let i = 0; i < places.length - 1; i++) {
        const line = new maps.Polyline({
          path: places.slice(i, i + 2),
          strokeColor: util.color,
          strokeOpacity: linesVisible ? util.opacity : 0,
          strokeWeight: util.width,
          icons: linesVisible ? [{
            // icon: { path: maps.SymbolPath.FORWARD_CLOSED_ARROW },
            icon: util.arrow,
            offset: '100%', // 100% means at end of line segment
          }] : [],
        })
        line.setMap(map)
        newLines.push(line)
      }
    }
    if (save.lines) {
      save.lines.forEach(line => line.setMap(null)) // remove old lines
    }
    return newLines
  }


  //----------------------------------------------------------------------
  // googlemapreact component
  //----------------------------------------------------------------------

  // need both class and id for map div
  return (
    <div className="map" id="map">
      <GoogleMapReact
        bootstrapURLKeys={keys}
        defaultCenter={view.center}
        defaultZoom={view.zoom}
        onChange={handleChange}
        options={util.createMapOptions}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
      >
        {/* {props.places.map((place, i) => (
          <div
            className="map-marker"
            key={place.name + i}
            text={place.name}
            lat={place.lat}
            lng={place.lng}
          >
            <img src={marker} alt="" />
          </div>
        ))} */}
      </GoogleMapReact>
    </div>
  )
})
